import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { clearAvatarSelection, isAvatarSelectionDirty, markAvatarSelectionSynced, readAvatarSelection, writeAvatarSelection } from '../utils/avatar'
import type { AvatarSelection } from '../utils/avatar'

interface User {
  id: number
  username: string
  email: string
  role: string
  vipLevel?: string
  levelName?: string
  rank?: string
  vipExpiry?: string
  phone?: string
  status?: number
  permissions?: string[]
  points?: number
  lastLoginTime?: string
  createdAt?: string
  avatar?: AvatarSelection | null
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (user: User, token: string) => void
  logout: () => void
  updateUser: (user: Partial<User>) => void
  setUser: (user: User | null) => void
}

const syncAvatarSelectionToLocal = (userId: number | null | undefined, avatar: AvatarSelection | null | undefined) => {
  if (!userId) return
  if (typeof avatar === 'undefined') return
  const dirty = isAvatarSelectionDirty(userId)
  if (dirty) {
    const local = readAvatarSelection(userId)
    if (
      avatar &&
      local &&
      (avatar.type === 'preset' || avatar.type === 'custom') &&
      avatar.type === local.type &&
      avatar.value === local.value
    ) {
      markAvatarSelectionSynced(userId)
    }
    return
  }
  if (avatar === null) {
    clearAvatarSelection(userId, { dirty: false })
    markAvatarSelectionSynced(userId)
    return
  }
  if (avatar.type === 'preset' || avatar.type === 'custom') {
    writeAvatarSelection(userId, avatar, { dirty: false })
    markAvatarSelectionSynced(userId)
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      login: (user, token) => {
        localStorage.setItem('token', token)
        syncAvatarSelectionToLocal(user?.id, user?.avatar)
        set({ user, token, isAuthenticated: true })
      },
      logout: () => {
        localStorage.removeItem('token')
        set({ user: null, token: null, isAuthenticated: false })
        window.location.href = '/login'
      },
      updateUser: (userData) => set((state) => {
        if (state.user && Object.prototype.hasOwnProperty.call(userData, 'avatar')) {
          syncAvatarSelectionToLocal(state.user.id, userData.avatar)
        }
        return { user: state.user ? { ...state.user, ...userData } : null }
      }),
      setUser: (user) => {
        syncAvatarSelectionToLocal(user?.id, user?.avatar)
        set({ user })
      },
    }),
    {
      name: 'auth-storage',
    }
  )
)

export const getAuthState = (): AuthState => {
  const store = useAuthStore as unknown as { getState: () => AuthState }
  return store.getState()
}
